<template>
  <div ref="study" class="access-study index-ignore" data-image-enlarge="off">
    <nav class="access-entries" aria-label="Site demonstrations">
      <section v-for="group in groups" :key="group.id" class="access-entry-group" :class="{ 'is-hidden': hiddenGroup(group) }" :inert="hiddenGroup(group)" :aria-hidden="hiddenGroup(group)">
      <div class="access-group-clip">
      <h4><template v-if="group.id === 'tools'"><ArchiveTerm kind="search" />, <AiAskTerm /> and <AiViewTerm /></template><template v-else><ArchiveTerm kind="gallery" /> and <ArchiveTerm kind="exhibition" /></template></h4>
      <div class="access-group-options">
      <div v-for="entry in group.entries" :key="entry.id" class="access-entry-slot" :class="{ 'is-hidden': hiddenEntry(entry) }" :inert="hiddenEntry(entry)" :aria-hidden="hiddenEntry(entry)">
        <div class="access-entry-clip">
          <button type="button" :aria-pressed="selectedScene === entry.id" @click="selectEntry(entry.id)">
            <span class="access-entry-number">{{ entryNumber(entry) }}</span>
            <span class="access-entry-copy"><strong>{{ entry.label }}</strong><span>{{ entry.description }}</span></span>
            <span class="access-progress" aria-hidden="true"><i :style="{ transform: `scaleX(${scene === entry.id ? elapsed / duration : 0})` }" /></span>
          </button>
        </div>
      </div>
      </div></div></section>
    </nav>
    <div class="access-film">
      <div class="access-toolbar">
        <div class="access-devices" role="group" aria-label="Preview device">
          <button v-for="option in ['desktop', 'phone']" :key="option" type="button" :aria-pressed="device === option" @click="selectDevice(option)">{{ option === 'phone' ? 'Phone' : 'Desktop' }}</button>
        </div>
        <div class="access-orientation-slot" :class="{ 'is-visible': device === 'phone' }" :inert="device !== 'phone'" :aria-hidden="device !== 'phone'">
          <div class="access-orientations" role="group" aria-label="Phone orientation">
            <button v-for="option in ['portrait', 'landscape']" :key="option" type="button" :aria-pressed="orientation === option" @click="selectOrientation(option)">{{ option === 'portrait' ? 'Portrait' : 'Landscape' }}</button>
          </div>
        </div>
        <button class="access-playback" type="button" :aria-label="finished ? 'Replay demonstration' : paused ? 'Play demonstration' : 'Pause demonstration'" @click="togglePlayback">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path v-if="finished" d="M4 7a6.5 6.5 0 1 1-.1 6M4 3v4h4"/><path v-else-if="paused" d="m7 4 8 6-8 6Z"/><path v-else d="M7 4v12M13 4v12"/></svg>
        </button>
      </div>
      <div class="access-mobile-picker">
        <ExhibitionDesignPicker :studies="pickerGroups" :value="selectedScene" label="Demonstration" :icons="false" :columns="1" @select="selectEntry" @open-change="pickerOpen = $event">
          <template #group-label="{ group }"><template v-if="group.id === 'tools'"><ArchiveTerm kind="search" />, <AiAskTerm /> and <AiViewTerm /></template><template v-else><ArchiveTerm kind="gallery" /> and <ArchiveTerm kind="exhibition" /></template></template>
        </ExhibitionDesignPicker>
        <span class="access-picker-progress" aria-hidden="true"><i :style="{ transform: `scaleX(${elapsed / duration})` }"/></span>
      </div>
      <div ref="stage" class="access-stage" :aria-busy="!ready">
        <div class="access-window" :class="{ 'is-phone': renderedDevice === 'phone', 'is-changing': changing || !ready }" :style="frameStyle">
          <ArchiveDemoScreen :active="loaded" :compact="renderedDevice === 'phone'" :landscape="renderedOrientation === 'landscape'" :visible="visible" :scene="scene" :state="state" :playing="playing" :reduced-motion="reducedMotion" @ready="frameReady = true" @scene-ready="readyScene = $event" />
        </div>
        <span v-if="loaded && !ready" class="access-loading" role="status">Preparing preview<span aria-hidden="true"/></span>
      </div>
      <p class="access-caption">{{ caption }}</p>
    </div>
  </div>
</template>
<script>
import { demoPreloadMargin } from '../../utils/demoPreload';
import ArchiveDemoScreen from './ArchiveDemoScreen.vue';
import ExhibitionDesignPicker from './ExhibitionDesignPicker.vue';
import AiAskTerm from './AiAskTerm.vue';
import AiViewTerm from './AiViewTerm.vue';
import ArchiveTerm from './ArchiveTerm.vue';
import { archiveDemoState, archiveDemoDuration } from '../../utils/archiveDemoTimeline';
import { ACCESS_ENTRIES, accessEntries, accessViewport, accessSceneForDevice } from '../../utils/siteAccessDemo';

export default {
  name: 'SiteAccessStudy', components: { ArchiveDemoScreen, ExhibitionDesignPicker, AiAskTerm, AiViewTerm, ArchiveTerm },
  data: () => ({
    scene: 'access-home', selectedScene: 'access-home', device: 'desktop', orientation: 'portrait',
    renderedDevice: 'desktop', renderedOrientation: 'portrait',
    elapsed: 0, paused: false, pickerOpen: false, frameReady: false, readyScene: '',
    stageWidth: 640, stageHeight: 480, loaded: false, visible: false, pageVisible: true, changing: false, reducedMotion: false,
  }),
  computed: {
    groups() { return [{ id: 'tools', label: 'Search, Ask and View' }, { id: 'spaces', label: 'Gallery and Exhibition' }].map(group => ({ ...group, entries: ACCESS_ENTRIES.filter(entry => entry.group === group.id) })); },
    pickerGroups() { return this.groups.map(group => ({ id: group.id, title: group.label, features: group.entries.filter(entry => !this.hiddenEntry(entry)) })).filter(group => group.features.length); },
    entries() { return accessEntries(this.device, this.orientation); },
    ready() { return this.frameReady && (!['access-gallery', 'access-exhibition'].includes(this.scene) || this.readyScene === this.scene); },
    duration() { return archiveDemoDuration(this.scene, this.renderedDevice === 'phone'); },
    state() { return archiveDemoState(this.scene, this.elapsed, this.renderedDevice === 'phone'); },
    finished() { return this.scene === this.entries[this.entries.length - 1].id && this.elapsed >= this.duration; },
    playing() { return this.ready && this.visible && this.pageVisible && !this.paused && !this.pickerOpen && !this.changing && !this.finished; },
    frameStyle() {
      const { width, height } = accessViewport(this.renderedDevice, this.renderedOrientation);
      const inset = this.renderedDevice === 'phone' ? 28 : 2;
      const scale = Math.min((this.stageWidth - inset) / width, (this.stageHeight - inset) / height);
      return { width: `${width * scale}px`, height: `${height * scale}px` };
    },
    caption() {
      if (this.scene === 'access-sidebar') return 'Search stays beside the page as you read.';
      if (this.scene === 'access-gallery') return 'Browse the shared Views, then approach an exhibition.';
      if (this.scene === 'access-exhibition') return 'Move between the original work and the reading that connects it.';
      return this.state.caption;
    },
  },
  watch: { playing() { this.syncClock(); } },
  mounted() {
    if (matchMedia('(max-width: 760px)').matches) this.device = this.renderedDevice = 'phone';
    this._motion = matchMedia('(prefers-reduced-motion: reduce)');
    this._motionChange = () => { this.reducedMotion = this._motion.matches; if (this.reducedMotion) this.paused = true; };
    this._motionChange(); this._motion.addEventListener('change', this._motionChange);
    this._visibility = () => { this.pageVisible = !document.hidden; };
    this._visibility(); document.addEventListener('visibilitychange', this._visibility);
    this._resize = new ResizeObserver(([entry]) => { this.stageWidth = entry.contentRect.width; this.stageHeight = entry.contentRect.height; });
    this._resize.observe(this.$refs.stage);
    this._observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting && entry.intersectionRatio >= .35; }, { threshold: [0, .35] });
    this._observer.observe(this.$refs.stage);
    this._preload = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { this.loaded = true; this._preload.disconnect(); } }, { rootMargin: demoPreloadMargin() });
    this._preload.observe(this.$refs.study);
  },
  beforeUnmount() {
    cancelAnimationFrame(this._frame); clearTimeout(this._swap); clearTimeout(this._reveal);
    this._observer?.disconnect(); this._preload?.disconnect(); this._resize?.disconnect();
    this._motion?.removeEventListener('change', this._motionChange);
    document.removeEventListener('visibilitychange', this._visibility);
  },
  methods: {
    hiddenEntry(entry) { return !this.entries.some(item => item.id === entry.id); },
    hiddenGroup(group) { return !group.entries.some(entry => !this.hiddenEntry(entry)); },
    entryNumber(entry) { return String(Math.max(1, this.entries.filter(item => item.group === entry.group).findIndex(item => item.id === entry.id) + 1)).padStart(2, '0'); },
    syncClock() {
      cancelAnimationFrame(this._frame);
      if (!this.playing) return;
      let last = performance.now();
      const tick = now => {
        if (!this.playing) return;
        this.elapsed = Math.min(this.duration, this.elapsed + Math.min(now - last, 80)); last = now;
        if (this.elapsed >= this.duration && !this.finished) { this.selectEntry(this.entries[this.entries.findIndex(entry => entry.id === this.scene) + 1].id); return; }
        if (!this.finished) this._frame = requestAnimationFrame(tick);
      };
      this._frame = requestAnimationFrame(tick);
    },
    selectEntry(scene) {
      this.selectedScene = scene;
      this.changePreview(true);
    },
    selectDevice(device) {
      if (this.device === device) return;
      this.device = device;
      this.selectedScene = accessSceneForDevice(this.selectedScene, device, this.orientation);
      this.changePreview(true);
    },
    selectOrientation(orientation) {
      if (this.orientation === orientation) return;
      this.orientation = orientation;
      this.selectedScene = accessSceneForDevice(this.selectedScene, this.device, orientation);
      this.changePreview(true);
    },
    changePreview(restart = false) {
      clearTimeout(this._swap); clearTimeout(this._reveal); this.changing = true;
      this._swap = setTimeout(() => {
        const nextScene = this.selectedScene;
        if (nextScene !== this.scene || restart) {
          if (nextScene !== this.scene) this.readyScene = '';
          this.elapsed = 0; this.scene = nextScene; this.paused = this.reducedMotion;
        }
        this.renderedDevice = this.device;
        this.renderedOrientation = this.orientation;
        this._reveal = setTimeout(() => { this.changing = false; }, this.reducedMotion ? 0 : 320);
      }, this.reducedMotion ? 0 : 120);
    },
    togglePlayback() { if (this.finished) this.selectEntry(this.entries[0].id); else this.paused = !this.paused; },
  },
};
</script>
<style scoped>
.access-study { --access-line:color-mix(in srgb,var(--case-ink) 14%,transparent);display:grid;grid-template-columns:200px minmax(0,1fr);gap:clamp(1.5rem,3vw,3rem);align-items:center;margin:1.8rem 0 0;color:var(--case-ink); }
.access-study button { appearance:none;border:0;background:none;font:inherit;color:inherit;cursor:var(--site-interactive-cursor,pointer);user-select:none; }
.access-entries { display:grid;min-width:0; }
.access-entry-group { display:grid;grid-template-rows:1fr;min-width:0;transition:grid-template-rows 320ms cubic-bezier(.22,1,.36,1),opacity 140ms ease; }
.access-entry-group.is-hidden { grid-template-rows:0fr;opacity:0;pointer-events:none; }
.access-group-clip { min-height:0;overflow:hidden; }
.access-entry-group h4 { margin:0;padding:20px 10px 8px;color:var(--case-muted);font:12px/1.4 'Quattrocento Sans',sans-serif;letter-spacing:.015em; }
.access-entry-slot { display:grid;grid-template-rows:1fr;opacity:1;transition:grid-template-rows 320ms cubic-bezier(.22,1,.36,1),opacity 140ms ease; }
.access-entry-slot.is-hidden { grid-template-rows:0fr;opacity:0;pointer-events:none; }
.access-entry-clip { min-height:0;overflow:hidden; }
.access-entries button { position:relative;display:flex;align-items:baseline;gap:12px;width:100%;box-sizing:border-box;padding:12px 10px;text-align:left;border-radius:3px;transition:background-color 140ms; }
.access-entries button[aria-pressed=true] { background:color-mix(in srgb,var(--case-ink) 5%,transparent); }
.access-entry-number { color:var(--case-muted);font:11px/1.5 'Quattrocento Sans',sans-serif; }
.access-entry-copy { display:grid;gap:5px; }
.access-entry-copy strong { font:400 17px/1.35 'PT Serif',Georgia,serif; }
.access-entry-copy>span { color:var(--case-muted);font:13px/1.5 'Quattrocento Sans',sans-serif; }
.access-progress { position:absolute;bottom:0;left:34px;right:10px;height:1px;overflow:hidden; }
.access-progress i { display:block;height:100%;background:currentColor;transform-origin:left;opacity:.55; }
.access-film { min-width:0; }
.access-mobile-picker { display:none;position:relative;margin:0 0 14px; }
.access-picker-progress { position:absolute;bottom:0;left:15px;right:15px;height:1px;pointer-events:none;overflow:hidden; }
.access-picker-progress i { display:block;height:100%;background:var(--case-muted);opacity:.6;transform-origin:left; }
.access-toolbar { display:flex;align-items:center;gap:20px;margin-bottom:12px;min-height:36px; }
.access-devices,.access-orientations { display:flex;align-items:center;gap:16px;white-space:nowrap; }
.access-devices button,.access-orientations button { min-height:36px;padding:8px 0;color:var(--case-muted);font:13px/1 'Quattrocento Sans',sans-serif; }
.access-devices [aria-pressed=true],.access-orientations [aria-pressed=true] { color:var(--case-ink);text-decoration:underline;text-underline-offset:7px;text-decoration-thickness:1px; }
.access-orientation-slot { display:grid;grid-template-columns:0fr;opacity:0;min-width:0;transition:grid-template-columns 300ms cubic-bezier(.22,1,.36,1),opacity 140ms ease; }
.access-orientation-slot.is-visible { grid-template-columns:1fr;opacity:1; }
.access-orientations { overflow:hidden;min-width:0; }
.access-playback { display:grid;place-items:center;flex:none;width:32px;height:36px;margin-left:auto;border-radius:50%!important; }
.access-playback svg { width:16px;height:16px;fill:none;stroke:currentColor;stroke-width:1.3;stroke-linecap:round;stroke-linejoin:round; }
.access-stage { position:relative;display:grid;place-items:center;height:clamp(380px,42vw,500px);background:color-mix(in srgb,var(--case-ink) 3%,transparent);overflow:hidden; }
.access-window { pointer-events:none;position:relative;border:1px solid var(--access-line);box-sizing:content-box;overflow:hidden;transition:width 320ms cubic-bezier(.22,1,.36,1),height 320ms cubic-bezier(.22,1,.36,1),border-radius 320ms; }
.access-window.is-phone { border-radius:20px; }
.access-window :deep(.demo-screen-viewport) { opacity:1;transition:opacity 160ms ease; }
.access-window.is-changing :deep(.demo-screen-viewport) { opacity:0;transition-duration:100ms; }
.access-loading { position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;color:var(--case-muted);font:12px/1.4 'Quattrocento Sans',sans-serif; }
.access-loading>span { width:42px;height:1px;background:currentColor;opacity:.3; }
.access-caption { margin:.75rem 0 0;min-height:2.8em;color:var(--case-muted);font:13px/1.5 'Quattrocento Sans',sans-serif; }
.access-study button:focus-visible { outline:1px solid currentColor;outline-offset:-2px; }
@media(hover:hover) and (pointer:fine) {
  .access-entries button:hover { background:color-mix(in srgb,var(--case-ink) 3%,transparent); }
  .access-entries button[aria-pressed=true]:hover { background:color-mix(in srgb,var(--case-ink) 5%,transparent); }
  .access-playback:hover { background:color-mix(in srgb,var(--case-ink) 6%,transparent); }
  .access-devices button:hover,.access-orientations button:hover { color:var(--case-ink); }
}
@media(max-width:900px) {
  .access-study { grid-template-columns:1fr;gap:0; }
  .access-entries { display:none; }
  .access-mobile-picker { display:block; }
  .access-stage { height:440px; }
}
@media(max-width:540px) {
  .access-toolbar { gap:14px; }
  .access-devices,.access-orientations { gap:12px; }
  .access-devices button,.access-orientations button { font-size:12px;min-height:44px; }
  .access-playback { width:28px;height:44px; }
  .access-stage { height:420px; }
  .access-caption { font-size:12px; }
}
@media(prefers-reduced-motion:reduce) { .access-study * { transition:none!important; } }
</style>
